import jsPDF from 'jspdf';
import 'jspdf-autotable';
import stripNonUnicode from 'utils/stripNonUnicode';

const documentToPDF = ({
  inputPDF,
  document,
  patient,
  business,
  insurance,
  orderLine,
  diagnosis,
  systemSettings,
  facility,
  prescriber,
  orderNotes,
  signature,
  signatureDate,
  orderInsuranceParam,
  appInfo,
  account,
  values = {},
}) => new Promise((resolve) => {
  const defaultLineHeight = 1.2;
  const ptsPerMm = 2.834646;
  const defaultFontSize = 10;
  const defaultFontStyle = 'normal';
  const textColor = 0;
  const drawColor = 0;
  const lineHeightMm = defaultFontSize * (defaultLineHeight / ptsPerMm);
  const fieldsShift = { x: 0, y: 0 };
  // eslint-disable-next-line new-cap
  const doc = inputPDF || new jsPDF({
    orientation: document.Orientation || 'p',
    unit: 'mm',
    format: document.Format,
    lineHeight: defaultLineHeight,
  });
  doc.setCharSpace(10);
  doc.setFont('times', defaultFontStyle);
  doc.setFontSize(defaultFontSize);
  doc.setFontType(defaultFontStyle);
  doc.setTextColor(textColor);
  doc.setDrawColor(drawColor);
  const pagesToLoad = document.Pages.filter(item => !item.image);
  const render = () => {
    document.Pages.forEach((page, index) => {
      if (index !== 0 || inputPDF) {
        doc.addPage(document.Format, document.Orientation || 'p');
      }
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      if (values['Show Background Template'] !== false) {
        doc.addImage(page.image, 'JPEG', 0 + fieldsShift.x, 0 + fieldsShift.y, pageWidth, pageHeight);
      }
      if (page.Logo) {
        if (facility) {
          if (facility.Image && facility.Image['File Name']) {
            const logoImageSrc = `${facility.Image['File Url']}${facility.Image['File Name']}`;
            const logoImage = new Image();
            logoImage.src = logoImageSrc;
            logoImage.onload = function() {
              const logoImageProperties = doc.getImageProperties(logoImage);
              const logoImageRatio = logoImageProperties.height / logoImageProperties.width;
              const renderWidth = page.Logo.width;
              const renderHeight = logoImageRatio * renderWidth;
              doc.addImage(logoImageSrc, 'JPEG',
                page.Logo.x + fieldsShift.x, page.Logo.y + fieldsShift.y, renderWidth, renderHeight);
            }
          }
        } else {
          if (business.Image && business.Image['File Name']) {
            const logoImageSrc = `${business.Image['File Url']}${business.Image['File Name']}`;
            const logoImage = new Image();
            logoImage.src = logoImageSrc;
            logoImage.onload = function() {
              const logoImageProperties = doc.getImageProperties(logoImage);
              const logoImageRatio = logoImageProperties.height / logoImageProperties.width;
              const renderWidth = page.Logo.width;
              const renderHeight = logoImageRatio * renderWidth;
              doc.addImage(logoImageSrc, 'JPEG',
                page.Logo.x + fieldsShift.x, page.Logo.y + fieldsShift.y, renderWidth, renderHeight);
            }
          }
        }
      }
      if (page.fields) {
        page.fields.forEach(({ name, x, y, width, height = 5.1, getValue, type, options, textAlign }) => {
          const maxWidth = width || pageWidth - x;
          let drawY = y;
          const drawX = textAlign === 'right' ? (x + width) : x;
          let value;
          if (getValue) {
            value = stripNonUnicode(getValue({
              patient,
              business,
              insurance,
              orderLine,
              diagnosis,
              systemSettings,
              facility,
              prescriber,
              orderNotes,
              orderInsuranceParam,
              signature,
              signatureDate,
              account,
              value: values ? values[name] : null,
            }));
          } else if (values) {
            value = values[name];
          }
          if (value) {
            switch (type) {
              case 'radio': {
                const selectedOption = options.find(item => item.value === value);
                if (selectedOption) {
                  doc.rect(selectedOption.x + fieldsShift.x, selectedOption.y + fieldsShift.y, selectedOption.width, selectedOption.height, 'F');
                }
                break;
              }
              case 'checkbox':
                doc.rect(x + fieldsShift.x, y + fieldsShift.y, width, height, 'F');
                break;
              case 'signature':
                doc.addImage(value, 'PNG', x + fieldsShift.x, y + fieldsShift.y, width, height);
                doc.setFontSize(7);
                doc.setFontType('italic');
                doc.text('Signed electronically', x + fieldsShift.x + width, y + fieldsShift.y + height, null, null, 'right');
                doc.setFontSize(defaultFontSize);
                doc.setFontType(defaultFontStyle);
                break;
              default: {
                const textLines = doc.splitTextToSize(value.toString(), maxWidth);
                textLines.forEach((line) => {
                  drawY += lineHeightMm;
                  if (drawY - y <= height) {
                    doc.text(line, drawX + fieldsShift.x, drawY + fieldsShift.y, null, null, textAlign);
                  }
                });
              }
            }
          }
        });
      }
      if (page.texts) {
        page.texts.forEach(({
          getText, x, y, width, align = 'left', fontStyle, fontWeight, fontSize, lineHeight,
        }) => {
          const text = stripNonUnicode(getText({
            patient,
            business,
            insurance,
            orderLine,
            diagnosis,
            systemSettings,
            facility,
            prescriber,
            orderNotes,
            orderInsuranceParam,
            signature,
            signatureDate,
            account,
          }));
          const maxWidth = width || pageWidth - x;
          let drawY = y;
          const drawX = x;
          let drawLineHeightMm = lineHeightMm;
          if (fontStyle) {
            if (fontStyle === 'italic' && fontWeight === 'bold') {
              doc.setFont('times', 'bolditalic');
            } else {
              doc.setFont('times', fontStyle);
            }
          } else if (fontWeight === 'bold') {
            doc.setFont('times', 'bold');
          } else {
            doc.setFont('times', 'normal');
          }
          if (fontSize) {
            doc.setFontSize(fontSize);
            drawLineHeightMm = fontSize * (lineHeight / ptsPerMm);
          }
          const textLines = doc.splitTextToSize(text, maxWidth);
          textLines.forEach((line) => {
            drawY += drawLineHeightMm;
            doc.text(line, drawX + fieldsShift.y, drawY + fieldsShift.y, null, null, align);
          });
          if (fontStyle) {
            doc.setFont('times', defaultFontStyle);
          }
          if (fontSize) {
            doc.setFontSize(defaultFontSize);
          }
        });
      }
    });

    resolve(doc);
  };
  if (pagesToLoad.length > 0) {
    const pageImagesPromises = document.Pages.map((page, index) => (
      import(`../${page.imageUrl}`)
        .then((image) => {
          // eslint-disable-next-line
          document.Pages[index].image = image.default;
        })));

    Promise.all(pageImagesPromises).then(() => {
      render();
    });
  } else {
    render();
  }
});

export default documentToPDF;



// WEBPACK FOOTER //
// ./src/utils/documentToPDF.js